import { inject, Injectable } from '@angular/core';
import { type CanDeactivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SettingsConfirmService } from '../kit/settings-confirm.service';

export interface SettingsDirtyPage {
  hasUnsavedChanges(): boolean;
}

/** Transient router metadata lets a cancelled external action preserve the Settings window. */
export class SettingsNavigationCancellation {
  public constructor(public readonly signal: AbortSignal) {}
}

export const SETTINGS_DISCARD_CONFIRMATION = {
  heading: 'Discard changes?',
  body: 'Your unsaved changes will be lost.',
  confirmLabel: 'Discard',
} as const;

/*
 * Closing the window moves history, and Angular's default `canceledNavigationResolution: 'replace'`
 * restores the URL of a cancelled navigation but not the history position — so the window has to ask
 * BEFORE it moves anything, not after. It then hands that answer to this guard through the gate: one
 * grant, consumed once, so a single gesture never asks the same question twice. Scoped to the
 * settings route, like the confirmation service, so it lives and dies with the window.
 */
@Injectable()
export class SettingsDiscardGate {
  private granted = false;

  public grant(): void {
    this.granted = true;
  }

  public revoke(): void {
    this.granted = false;
  }

  public consume(): boolean {
    const granted = this.granted;
    this.granted = false;
    return granted;
  }
}

export const settingsSubpageCanDeactivateGuard: CanDeactivateFn<SettingsDirtyPage> = (component) => {
  const info = inject(Router, { optional: true })?.currentNavigation()?.extras.info;
  const signal = info instanceof SettingsNavigationCancellation ? info.signal : undefined;
  const alreadyAsked = inject(SettingsDiscardGate).consume();
  if (signal?.aborted) return false;
  if (alreadyAsked || !component.hasUnsavedChanges()) {
    return true;
  }
  const confirmation = inject(SettingsConfirmService);
  return firstValueFrom(
    signal
      ? confirmation.confirm({ ...SETTINGS_DISCARD_CONFIRMATION }, signal)
      : confirmation.confirm({ ...SETTINGS_DISCARD_CONFIRMATION }),
  );
};
